import React from "react";
import { Elements } from "@stripe/react-stripe-js";
import NumberPad from "./NumberPad";
import YouPayLine from "./YouPayLine";
import InlinePayOptions from "./InlinePayOptions";
import { BanknoteIcon } from "./GreenIcons";
import { paymentsApi } from "../services/payments";
import { currencySymbol, formatSingleAmount, formatTxnAmount } from "../utils/currency";
import { SUFFIX_CUR } from "../utils/requestsFormat";
import { STRIPE_PK, stripePromise } from "../lib/fanCheckout";
import type { TipCheckout as TipCheckoutState } from "../hooks/useTipCheckout";
import { t as tr } from "../i18n";

// The tip section: three tiles + Custom pad, the Send Tip CTA, then the
// payment reveal (loading placeholders, Stripe Elements, the local mock).
// Split out of pages/RequestPage.tsx; the state lives in hooks/useTipCheckout.

type Props = {
  displayDj: any;
  isProfilePreview: boolean;
  emailForLink: string;
  setEmailForLink: (v: string) => void;
  tip: TipCheckoutState;
};

export default function TipCheckout({ displayDj, isProfilePreview, emailForLink, setEmailForLink, tip }: Props) {
  const {
    tipMode,
    setTipPadOpen,
    tipPadOpen,
    tipDraft,
    setTipDraft,
    setTipAmount,
    tipCtaTapped,
    tipOrderId,
    tipClientSecret,
    tipShowPay,
    tipShowCard,
    setTipShowCard,
    tipProcessing,
    setTipProcessing,
    tipPayError,
    setTipPayError,
    tipBase,
    tipTotal,
    tipTiles,
    tipTileSelected,
    tipIsCustom,
    commitTipPad,
    tipSucceeded,
    tipWalletMark,
    startTipPurchase,
  } = tip;
  return (
    <>
      {tipMode && !isProfilePreview && (
        <section className="rq-tip-section">
          {/* The yellow "TIP ONLY — NO REQUEST" banner is gone (founder
              8/29: redundant — the card says who you're tipping). */}
          {/* Facelift (founder pick A): exact Buy Playlist twin —
              💸 header, six emoji tiles that get happier as the tip grows,
              Custom → NumberPad, wallet-direct CTA. */}
          <div className="rq-bid-card">
            <div className="rq-buy-money" aria-hidden><BanknoteIcon size={34} /></div>
            <div className="rq-bid-title">{tr("tipCheckout.tipName", { name: displayDj.name ?? "" })}</div>
            <div className="rq-buy-hint rq-buy-hint--top">{tr("tipCheckout.everyDollarGoesLoud")}</div>
            <div className="rq-buy-tiles">
              {tipTiles.map((t) => (
                <button
                  key={t.amount}
                  type="button"
                  className={`rq-buy-tile${tipTileSelected(t.amount) ? " on" : ""}`}
                  onClick={() => {
                    setTipPadOpen(false);
                    setTipDraft("");
                    setTipAmount(String(t.amount));
                  }}
                >
                  <span className="rq-buy-tile-emoji" aria-hidden><t.Icon size={22} /></span>
                  <span className="rq-buy-tile-amt">{formatTxnAmount(t.amount, displayDj.currency)}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`rq-buy-custom${tipIsCustom ? " on" : ""}`}
              onClick={() => {
                setTipDraft("");
                setTipPadOpen(true);
              }}
            >
              {tipPadOpen && tipDraft
                ? tr("tipCheckout.custom", { value: !SUFFIX_CUR.has((displayDj.currency || "usd").toLowerCase()) ? currencySymbol(displayDj.currency) : "", tipDraft, value2: SUFFIX_CUR.has((displayDj.currency || "usd").toLowerCase()) ? ` ${currencySymbol(displayDj.currency)}` : "" })
                : tipIsCustom
                ? tr("tipCheckout.custom2", { amount: formatTxnAmount(tipBase, displayDj.currency) })
                : tr("tipCheckout.customAmount")}
            </button>
            <button
              type="button"
              className="rq-buy-cta"
              onClick={startTipPurchase}
            >
              {tipWalletMark ? " " : ""}{tr("tipCheckout.sendTipAmount", { amount: formatTxnAmount(tipBase, displayDj.currency) })}
            </button>
            <div className="rq-buy-trust">
              {/* Legal-safe family: the DJ nets exactly 85%,
                  so "tipping directly" — never "go directly to". */}
              {tr("tipCheckout.youReTippingDirectly", { name: displayDj.name ?? "" })}
            </div>
          </div>

          {tipCtaTapped && (
            <YouPayLine
              variant="panel"
              totalLabel={formatSingleAmount(tipTotal, displayDj.currency)}
            />
          )}

          {tipCtaTapped && !tipClientSecret && !tipPayError && (
            <div
              className="rq-inline-pay rq-inline-pay--inpaybox rq-pay-loading-placeholders"
              aria-busy="true"
              aria-label={tr("tipCheckout.loadingPaymentOptions")}
            >
              <div className="rq-inline-pay">
                <div className="rq-wallets">
                  <div className="rq-express-vertical">
                    <div className="rq-fake-express-slot">
                      <span className="rq-fake-express-slot__label">{tr("tipCheckout.loadingPayment")}</span>
                    </div>
                  </div>
                  <div className="rq-add-card" aria-hidden="true">
                    {tr("tipCheckout.addCardDetails")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tipCtaTapped && tipShowPay && stripePromise && tipClientSecret && (
            <Elements
              key={`tip-el-${tipClientSecret}-${tipTotal.toFixed(2)}`}
              stripe={stripePromise}
              options={{
                clientSecret: tipClientSecret,
                appearance: { theme: "night" },
                locale: "en",
              }}
            >
              <div className="rq-inline-pay rq-inline-pay--inpaybox">
                <InlinePayOptions
                  key={`tip-ipo-${tipClientSecret}-${tipTotal.toFixed(2)}`}
                  orderId={tipOrderId}
                  emailForLink={emailForLink}
                  setEmailForLink={setEmailForLink}
                  showCardForm={tipShowCard}
                  setShowCardForm={setTipShowCard}
                  processing={tipProcessing}
                  setProcessing={setTipProcessing}
                  payError={tipPayError}
                  setPayError={setTipPayError}
                  selectedTrack={null}
                  totalToCharge={tipTotal}
                  context="tip"
                  djName={displayDj.name}
                  clientSecret={tipClientSecret}
                  currency={displayDj.currency}
                  hideWallets
                  onPaymentSuccess={async (piId?: string) => {
                    if (!tipOrderId) return;
                    await tipSucceeded(tipOrderId, piId);
                  }}
                />
              </div>
            </Elements>
          )}

          {tipCtaTapped && tipShowPay && !STRIPE_PK && tipOrderId && (
            <div className="rq-inline-pay rq-inline-pay--inpaybox" style={{ padding: "16px", textAlign: "center" }}>
              <p style={{ marginBottom: 12, color: "#aaa", fontSize: 14 }}>{tr("tipCheckout.localDevMode")}</p>
              <button
                style={{
                  background: "#1db954",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 32px",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: tipProcessing ? "not-allowed" : "pointer",
                  opacity: tipProcessing ? 0.6 : 1,
                }}
                disabled={tipProcessing}
                onClick={async () => {
                  setTipProcessing(true);
                  try {
                    await paymentsApi.mockPay(tipOrderId);
                    window.location.href = `/fan/requests?order=${encodeURIComponent(tipOrderId)}`;
                  } catch (e: any) {
                    setTipPayError(e?.response?.data?.msg || tr("tipCheckout.mockPaymentFailed"));
                    setTipProcessing(false);
                  }
                }}
              >
                {tipProcessing ? tr("tipCheckout.processing") : tr("tipCheckout.mockTip", { amount: formatSingleAmount(tipTotal, displayDj.currency) })}
              </button>
            </div>
          )}

          {tipPayError && !tipShowPay && (
            <div className="rq-pay-error">{tipPayError}</div>
          )}

          <NumberPad
            open={tipPadOpen}
            value={tipDraft}
            onChange={setTipDraft}
            onDone={commitTipPad}
            allowDecimal
            withBackdrop
            maxLength={8}
          />
        </section>
      )}
    </>
  );
}
